import { AUTHORABLE_PROMPT_ROLES, PROMPT_ROLE_ALL } from '@developerz/domain';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type PromptRoleChoice, SYSTEM_PROMPT_MAX } from '../lib/system-prompts';
import { Field } from './Field';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

// One layer of the system-prompt cascade (#637) as a control-plane prompt card
// (mock `PromptCard`): card header (title sm + desc), `.textarea` with a mono
// counter underneath, full-bleed footer with Save + Clear. Pure presenter — the
// containers (`SystemPrompts`, `RepoPromptCard`) own fetching + mutations.
//
// The role picker (plan 101 slice 06) chooses WHICH layer the textarea edits:
// every run, or one run type. Its options are every run plus the domain's
// AUTHORABLE allowlist, so `triage` (platform-only) is never offered.
export type SystemPromptEditorProps = {
  /** Prefix for this card's control ids — unique per page. */
  readonly id: string;
  readonly title: string;
  readonly description: string;
  /** The stored text for the current role — what "dirty" is measured against. */
  readonly value: string;
  /** The text being edited; the container keeps it per role (`usePromptLayer`). */
  readonly draft: string;
  readonly onDraft: (text: string) => void;
  readonly busy: boolean;
  readonly error: string | null;
  readonly savedText: string;
  readonly role: PromptRoleChoice;
  readonly onRoleChange: (role: PromptRoleChoice) => void;
  readonly onSave: (text: string) => void;
  readonly onClear: () => void;
};

const NUM = new Intl.NumberFormat('en-US');

export function SystemPromptEditor(props: SystemPromptEditorProps): JSX.Element {
  const draft = (): string => props.draft;

  const over = (): boolean => draft().length > SYSTEM_PROMPT_MAX;
  const dirty = (): boolean => draft() !== props.value;
  const submit = (e: Event): void => {
    e.preventDefault();
    if (over() || props.busy) return;
    props.onSave(draft());
  };

  return (
    <form class="card card--footered prompt-card" aria-label={props.title} onSubmit={submit}>
      <div class="card__header">
        <h2 class="card__title">{props.title}</h2>
        <p class="card__desc">{props.description}</p>
      </div>

      <div class="card__content prompt-card__body">
        <Field
          id={`${props.id}-role`}
          label={t('system_prompts.role_label')}
          desc={t('system_prompts.role_hint')}
        >
          {(a11y) => (
            <Select<PromptRoleChoice>
              a11y={a11y}
              label={t('system_prompts.role_label')}
              block
              disabled={props.busy}
              value={props.role}
              options={[
                { value: PROMPT_ROLE_ALL, label: t('system_prompts.role.all') },
                ...AUTHORABLE_PROMPT_ROLES.map((role) => ({
                  value: role,
                  label: t(`system_prompts.role.${role}`),
                })),
              ]}
              onChange={props.onRoleChange}
            />
          )}
        </Field>
        <textarea
          class="textarea"
          value={draft()}
          onInput={(e) => props.onDraft(e.currentTarget.value)}
          rows={5}
          spellcheck={false}
          aria-label={props.title}
          aria-invalid={over() ? 'true' : undefined}
        />

        <p class="prompt-card__meta">
          <Show when={props.savedText && !dirty()}>
            <span>{props.savedText}</span>
          </Show>
          <span class="prompt-card__count" classList={{ 'prompt-card__count--over': over() }}>
            {t('system_prompts.char_count', {
              count: NUM.format(draft().length),
              max: NUM.format(SYSTEM_PROMPT_MAX),
            })}
          </span>
        </p>

        <Show when={props.error}>
          <p class="field__error" role="alert">
            {props.error}
          </p>
        </Show>
      </div>

      <div class="card__footer prompt-card__footer">
        <button
          type="submit"
          class={buttonClass({ size: 'sm' })}
          disabled={props.busy || over() || !dirty()}
        >
          {props.busy ? t('system_prompts.saving') : t('system_prompts.save')}
        </button>
        <Show when={props.value !== ''}>
          <Button variant="ghost" size="sm" disabled={props.busy} onClick={() => props.onClear()}>
            {t('system_prompts.clear')}
          </Button>
        </Show>
      </div>
    </form>
  );
}
